'use client';

import React, { useRef, useState, useSyncExternalStore } from 'react';
import Slick from 'react-slick';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'use-intl';
import HeroBanner from '@/components/organisms/hero-banner';
import Slider from '@/components/organisms/slider';
import { classnames } from '@/utils/classnames/classnames';
import { HeroCarouselProps } from './types';

// EWM-760 (was a setState-in-effect: read `matchMedia` + setState on mount,
// then subscribe) — `prefers-reduced-motion` is a real external source, so
// this is a textbook useSyncExternalStore: subscribe to the media query's
// `change` event, read `.matches` as the client snapshot. Server snapshot is
// `false` (matches the old useState(false) initial value pre-effect).
const subscribeToReducedMotion = (callback: () => void) => {
  const query = window.matchMedia('(prefers-reduced-motion: reduce)');
  query.addEventListener('change', callback);
  return () => query.removeEventListener('change', callback);
};
const getReducedMotionSnapshot = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const getReducedMotionServerSnapshot = () => false;

const usePrefersReducedMotion = () =>
  useSyncExternalStore(subscribeToReducedMotion, getReducedMotionSnapshot, getReducedMotionServerSnapshot);

/**
 * HeroCarousel — a rotating carousel of full-width <HeroBanner> slides (EWM-455).
 * Wraps the shared react-slick `Slider` with custom overlay arrows + bottom-centre
 * dots (Dan's v0 mock), reduced-motion-safe autoplay (pauses on hover / focus-within),
 * keyboard nav (←/→ advance, Home/End jump) and an a11y live region. A single slide
 * renders as a static hero (no chrome); zero slides render nothing.
 */
const HeroCarousel = ({
  slides,
  autoplay = true,
  autoplayIntervalMs = 5000,
  showArrows = true,
  showDots = true,
  loop = true,
}: HeroCarouselProps) => {
  const translate = useTranslations();
  const sliderRef = useRef<Slick>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const reducedMotion = usePrefersReducedMotion();

  if (slides.length === 0) return null;

  // Single slide: behave as a static hero — no Slider, no arrows, no dots.
  if (slides.length === 1) return <HeroBanner {...slides[0]} />;

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') sliderRef.current?.slickPrev();
    else if (event.key === 'ArrowRight') sliderRef.current?.slickNext();
    else if (event.key === 'Home') sliderRef.current?.slickGoTo(0);
    else if (event.key === 'End') sliderRef.current?.slickGoTo(slides.length - 1);
    else return;
    event.preventDefault();
  };

  // Pause autoplay whenever the pointer or keyboard focus is anywhere inside the
  // carousel. The overlay arrows/dots sit OUTSIDE react-slick's own track, so its
  // built-in pauseOnHover/pauseOnFocus never see them — own pause/resume here so
  // hovering or tabbing onto a control reliably halts auto-advance.
  const pauseAutoplay = () => sliderRef.current?.slickPause();
  const resumeAutoplay = () => {
    if (autoplay && !reducedMotion) sliderRef.current?.slickPlay();
  };
  // Resume only when focus truly leaves the section — not when it moves between the
  // arrows/dots (which fires blur on the old control before focus on the new one).
  const onBlur = (event: React.FocusEvent) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) resumeAutoplay();
  };

  return (
    <section
      role="region"
      aria-roledescription="carousel"
      aria-label={translate('common.hero-carousel')}
      tabIndex={0}
      className="relative w-full overflow-hidden focus:outline-none"
      onKeyDown={onKeyDown}
      onMouseEnter={pauseAutoplay}
      onMouseLeave={resumeAutoplay}
      onFocus={pauseAutoplay}
      onBlur={onBlur}
    >
      <Slider
        ref={sliderRef}
        arrows={false}
        // We own keyboard nav on the section; disable react-slick's own arrow handler to avoid double-advance.
        accessibility={false}
        dots={false}
        infinite={loop}
        slidesToShow={1}
        // Full-bleed hero slides must butt edge-to-edge; the Slider's default 10px
        // inter-slide gutter would otherwise show as a white channel between slides.
        spaceBetween={0}
        autoplay={autoplay && !reducedMotion}
        autoplaySpeed={autoplayIntervalMs}
        // pauseOnHover/pauseOnFocus only cover react-slick's own track; the section
        // handlers above own pause/resume so the overlay arrows/dots are covered too.
        afterChange={(index: number) => setCurrentSlide(index)}
      >
        {slides.map((props, index) => (
          // Eager-load every slide's background up front (slide 0 is already `isPriority`)
          // so navigating to a slide doesn't flash white while its image lazy-loads in.
          <HeroBanner key={index} {...props} imageLoading="eager" />
        ))}
      </Slider>

      {showArrows &&
        (['prev', 'next'] as const).map((dir) => {
          const Icon = dir === 'prev' ? ChevronLeftIcon : ChevronRightIcon;
          return (
            <button
              key={dir}
              type="button"
              aria-label={translate(dir === 'prev' ? 'common.previous' : 'common.next')}
              onClick={() => (dir === 'prev' ? sliderRef.current?.slickPrev() : sliderRef.current?.slickNext())}
              className={classnames(
                'focus-visible:ring-primary absolute top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-lg transition-all hover:scale-105 hover:bg-white focus-visible:ring-2 focus-visible:outline-none md:size-12',
                dir === 'prev' ? 'left-4 md:left-6' : 'right-4 md:right-6',
              )}
            >
              <Icon aria-hidden className="size-5 md:size-6" />
            </button>
          );
        })}

      {showDots && (
        <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 gap-2 md:bottom-6">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={translate('common.go-to-slide', { number: index + 1 })}
              aria-current={index === currentSlide}
              onClick={() => sliderRef.current?.slickGoTo(index)}
              className={classnames(
                'size-2.5 rounded-full transition-all focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none md:size-3',
                index === currentSlide ? 'scale-110 bg-white' : 'bg-white/50 hover:bg-white/75',
              )}
            />
          ))}
        </div>
      )}

      {/* a11y: announce the active slide for screen readers on advance. */}
      <span aria-live="polite" className="sr-only">
        {translate('common.slide-x-of-y', { current: currentSlide + 1, total: slides.length })}
      </span>
    </section>
  );
};

export default HeroCarousel;
